import { useState, useEffect, useContext } from "react";
import { openDB } from "idb";
import { constrainLength, constrainLineLengths } from "../utils/form";
import styles from "../css/SetupForm.module.css";
import { getData, updateServiceStatus } from "../utils/api";
import Context from "../context/context"

const DEFAULT_PICKUP_SETUP = {
  message_service_available: "",
  message_service_unavailable: "",
  message_after_order: "",
  notification_sms_confirmation: "",
  notification_sms_order_ready: "",
  default_mode: "0",
  payment_modes: [],
  customer_location: "",
  minimum_pickup_time: "",
};

const DEFAULT_PAYMENT_MODES = [
  {
    _id: "61b30b858ded1602f2a23320",
    maintain_customer_balance: "Y",
    outlet_wise_details: [
      {
        _id: "61e3daa107ff0d681d8e06e9",
        outlet_uuid: "outlet-uuid-1",
        status: "Loading...",
      },
      { _id: "61e3daa107ff0d681d8e06ea", outlet_uuid: "", status: "" },
    ],
    payment_mode_name: "Loading...",
    payment_mode_uuid: "1",
  },
];

const PICKUP_SETUP_MAX_DIGITS = {
  minimum_pickup_time: 3,
};

const PICKUP_SETUP_MESSAGE_LINES = {
  message_service_available: [5, 40],
  message_service_unavailable: [5, 40],
  message_after_order: [5, 40],
};

export default function PickupSetupForm({ onSave }) {

  const [pickupSetup, setPickupSetup] = useState(DEFAULT_PICKUP_SETUP);
  const [dbPaymentModes, setDbPaymentModes] = useState(DEFAULT_PAYMENT_MODES);
  const [serviceStatus, setServiceStatus] = useState(2);
  const [online, setOnline] = useState(navigator.onLine);
  const context = useContext(Context);
  const {
    updateUnSync
  } = context;

  useEffect(() => async function asyncUseEffect() {
    console.log("runnign useeffect");
    const db = await openDB("FoodDo", +localStorage.getItem("IDBVersion") || 1);
    const paymentModeStore = db
      .transaction("payment_modes", "readonly")
      .objectStore("payment_modes");
    const paymentModes = await paymentModeStore.getAll();
    console.debug(paymentModes);
    setDbPaymentModes([
      ...paymentModes,
      { payment_mode_name: 'Cash on Delivery', payment_mode_uuid: "0", hardCoded: 1 },
      { payment_mode_name: 'UPI', payment_mode_uuid: "UPI", hardCoded: 1 },
    ]);

    const outletStore = db
      .transaction("outlet", "readonly")
      .objectStore("outlet");
    const currentOutlet = (await outletStore.getAll())[0];
    const dbPickupSetup = currentOutlet.pickup_setup;
    const filteredDbPickupSetup = Object.fromEntries(
      Object.entries(dbPickupSetup).filter(([key]) =>
        Object.keys(DEFAULT_PICKUP_SETUP).includes(key)
      )
    );
    setPickupSetup(
      unwrapDbPickupState({
        ...DEFAULT_PICKUP_SETUP,
        ...filteredDbPickupSetup,
      })
    );
    setOnline(navigator.onLine);
    const data = await getData();
    const [{ service_status }] = data.filter(
      (service) => service.service_name === "Pick-up" && service
    );
    setServiceStatus(service_status.toLowerCase() === "y" ? 0 : 1);

  }(), []);

  const getFormattedPickupSetup = () => ({
    ...pickupSetup,
    notification_sms_confirmation: String(
      Number(pickupSetup.notification_sms_confirmation)
    ),
    notification_sms_order_ready: String(
      Number(pickupSetup.notification_sms_order_ready)
    ),
  });

  const unwrapDbPickupState = (dbPickupState) => ({
    ...dbPickupState,
    notification_sms_confirmation: Boolean(
      Number(dbPickupState.notification_sms_confirmation)
    ),
    notification_sms_order_ready: Boolean(
      Number(dbPickupState.notification_sms_order_ready)
    ),
  });

  const handleChange = (event) => {
    const getValue = (e) => (e.type === "checkbox" ? e.checked : e.value);
    const getSelectedValues = (e) =>
      [...e.options].filter((e) => e.selected).map((e) => e.value);

    const value = event.target.multiple
      ? getSelectedValues(event.target)
      : getValue(event.target);

    const name = event.target.name;
    const isMessage = Object.keys(PICKUP_SETUP_MESSAGE_LINES).includes(name);
    const isNumber = Object.keys(PICKUP_SETUP_MAX_DIGITS).includes(name);
    const isPayment = name === "payment_modes";
    const constrainedValue = isMessage
      ? constrainLineLengths(value, ...PICKUP_SETUP_MESSAGE_LINES[name])
      : isNumber
        ? constrainLength(String(value), PICKUP_SETUP_MAX_DIGITS[name])
        : isPayment
          ? [...new Set(value), "0"] // always include "Cash on Delivery"
          : value;

    setPickupSetup({
      ...pickupSetup,
      [event.target.name]: constrainedValue,
    });
  };

  const handleMultipleSelections = (value) => {
    if (!pickupSetup.payment_modes.includes(value)) {
      setPickupSetup({
        ...pickupSetup,
        payment_modes: [...(
          dbPaymentModes
            ?.filter(i => pickupSetup?.payment_modes?.includes(i.payment_mode_uuid))
            ?.map(i => i.payment_mode_uuid) || []), value],
      });
    } else {
      const payment_modes = pickupSetup.payment_modes.filter(i => i !== value)
      const default_mode = pickupSetup?.default_mode === value || payment_modes?.length === 1
        ? payment_modes?.[0]
        : pickupSetup?.default_mode

      setPickupSetup({
        ...pickupSetup,
        default_mode,
        payment_modes,
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const db = await openDB("FoodDo", +localStorage.getItem('IDBVersion') || 1);
    const outletStore = db.transaction("outlet", "readwrite").objectStore("outlet");
    const value = (await outletStore.getAll())[0];
    const updatedValue = {
      ...value,
      pickup_setup: getFormattedPickupSetup(),
    };
    // console.log(updatedValue);
    await outletStore.put(updatedValue);
    handlePickupUnsyncedFile(updatedValue);
    onSave();
  };

  const handleStatusChange = async (e) => {
    const value = parseInt(e.target.value);
    const status = value === 0 ? "Y" : "N";
    setServiceStatus(value);
    await updateServiceStatus("Pick-up", { service_status: status });
  };

  // Adding/Editing Pickup to unsynced_files collection in IndexedDB
  const handlePickupUnsyncedFile = async (data) => {
    const db = await openDB("FoodDo", +localStorage.getItem("IDBVersion") || 1);
    const tx = await db.transaction("unsynced_files", "readwrite").objectStore("unsynced_files");
    const ndata = {
      IDENTIFIER: "pickup_setup",
      RECON_TIME: Date.now(),
      pickup_setup: data.pickup_setup,
    };
    await tx.put(ndata);
    handlePickupReconciliation(ndata);
    updateUnSync()
  };

  // Adding/Editing Pickup to reconciliation collection in IndexedDB
  const handlePickupReconciliation = async (data) => {
    const db = await openDB("FoodDo", +localStorage.getItem("IDBVersion") || 1);
    const tx = await db.transaction("reconciliation", "readwrite").objectStore("reconciliation");
    const allReconRecords = await tx.getAll();

    const othersJsonExists = allReconRecords.find((curr) => curr.IDENTIFIER === "others");
    if (othersJsonExists) {
      const pickup = othersJsonExists?.details?.filter((entry) => entry.type !== 'pickup_setup')
      const editData = {
        ...othersJsonExists,
        details: [
          ...pickup,
          {
            type: 'pickup_setup',
            RECON_TIME: data.RECON_TIME,
          },
        ],
      };
      await tx.put(editData)
    } else {
      const newdata = {
        IDENTIFIER: "others",
        details: [
          {
            type: 'pickup_setup',
            RECON_TIME: data.RECON_TIME,
          },
        ]
      };
      await tx.add(newdata);
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.heading}>
        <h1>Pickup Setup</h1>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.status}>
          <h5>Status</h5>
          <select
            name="service_status"
            className={styles.select}
            disabled={!online}
            value={serviceStatus}
            onChange={handleStatusChange}>
            {online ? (
              serviceStatus === 2 ? (
                <option value={2}>Loading...</option>
              ) : (
                <>
                  <option value={0}>Enabled</option>
                  <option value={1}>Disabled</option>
                </>
              )
            ) : (
              <option selected>Check your connection</option>
            )}
          </select>
        </div>

        <div className={styles.formGroup}>
          <h2>General</h2>

          <div className={styles.row}>
            <div className={styles.selectLabel}>
              <p style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Payment Modes</span>
                <span style={{ color: "#7f7f8b", fontSize: "small", marginTop: "8px", }}>Default</span>
              </p>
              <div className={`${styles.selections} ${styles.layout}`}>
                {Object.values(dbPaymentModes)
                  .filter(paymentMode => paymentMode.maintain_customer_balance === "Y" || paymentMode.hardCoded)
                  .map(({ payment_mode_uuid, payment_mode_name, ...paymentMode }) => {
                    const checked = pickupSetup.payment_modes.includes(payment_mode_uuid)
                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '5px' }}>
                        <input
                          type="checkbox"
                          name="payment-mode"
                          id={payment_mode_uuid}
                          checked={checked}
                          onChange={e => handleMultipleSelections(payment_mode_uuid)}
                          style={{ marginRight: '10px' }}
                          disabled={checked && pickupSetup.payment_modes.length === 1}
                        />
                        <label htmlFor={payment_mode_uuid} style={{ width: '100%' }}>
                          {payment_mode_name}
                        </label>
                        {checked && <input
                          type="radio"
                          name="default-payment-mode"
                          checked={pickupSetup.default_mode === payment_mode_uuid}
                          onChange={e => e.currentTarget.checked && checked && setPickupSetup(doc => ({
                            ...doc, default_mode: payment_mode_uuid
                          }))}
                        />}
                      </div>
                    )
                  })}

              </div>
            </div>

            <label className={styles.selectLabel}>
              Customer Location
              <select
                name="customer_location"
                className={styles.select}
                value={pickupSetup.customer_location}
                onChange={handleChange}>
                <option value={0}>Never ask</option>
                <option value={1}>Always ask before placing an order</option>
                <option value={2}>Ask on new address only</option>
              </select>
            </label>

            <label className={styles.selectLabel}>
              Minimum pickup time
              <input
                type="number"
                name="minimum_pickup_time"
                className={styles.numberInput}
                value={pickupSetup.minimum_pickup_time}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <h2>Display Message</h2>

          <div className={styles.row}>
            <label className={styles.selectLabel}>
              When service unavailable
              <textarea
                name="message_service_unavailable"
                className={styles.messageTextarea}
                value={pickupSetup.message_service_unavailable}
                onChange={handleChange}
              />
            </label>

            <label className={styles.selectLabel}>
              When service available
              <textarea
                name="message_service_available"
                className={styles.messageTextarea}
                value={pickupSetup.message_service_available}
                onChange={handleChange}
              />
            </label>

            <label className={styles.selectLabel}>
              After placing order
              <textarea
                name="message_after_order"
                className={styles.messageTextarea}
                value={pickupSetup.message_after_order}
                onChange={handleChange}
              />
            </label>
          </div>
        </div>

        <div className={styles.formGroup}>
          <h2>Notification Settings</h2>

          <div className={styles.row}>
            <label className={styles.checkboxLabel}>
              <input
                name="notification_sms_confirmation"
                type="checkbox"
                checked={pickupSetup.notification_sms_confirmation}
                onChange={handleChange}
              />
              Send SMS for Order Accept / Declined
            </label>

            <label className={styles.checkboxLabel}>
              <input
                name="notification_sms_order_ready"
                type="checkbox"
                checked={pickupSetup.notification_sms_order_ready}
                onChange={handleChange}
              />
              Send SMS for Order Ready
            </label>
          </div>
        </div>

        <button type="submit" className={styles.submit}>
          Save changes
        </button>
      </div>
    </form>
  );
}
